import { DUPLICATE_SHAPE_OFFSET, RGB_COLORS } from '@/constants/constants';
import { AppState } from '@/context/appContext';
import { addObjectToAppState } from '@/functions/renderOrderFunctions';
// Import directly from the module (not the '@/helpers' barrel) to avoid a circular-import
// cycle (shapesReducer → context/functions → appReducer → shapesReducer) that can leave the
// barrel's re-exported binding unresolved. appReducer imports pdf helpers the same way.
import { canvasPointsToPdfPoints } from '@/helpers/pdfHelpers';
import { Point } from '@/types';
import { Shape, ShapeTypes } from '@/types/shapes';
import { RGBColor } from '@/types/colors';
import { generateId } from '@/helpers/ids';

export enum ShapesReducerActionType {
  ADD_SHAPE = 'ADD_SHAPE',
  UPDATE_SHAPE = 'UPDATE_SHAPE',
  DELETE_SHAPE = 'DELETE_SHAPE',
  DUPLICATE_SHAPE = 'DUPLICATE_SHAPE',
}

export type ShapeAddPayload = {
  pageNumber: number;
  type: ShapeTypes;
  points: Point[];
  borderColor?: RGBColor;
  backgroundColor?: RGBColor;
  borderWidth?: number;
  // Rectangles only: marks a whiteout/erase rectangle, which locks the text it covers.
  isWhiteout?: boolean;
};

type ShapeUpdatePayload = {
  pageNumber: number;
  shape: { id: string } & Partial<Shape>;
};

export type ShapesReducerAction =
  | {
      type: ShapesReducerActionType.ADD_SHAPE;
      payload: ShapeAddPayload;
    }
  | {
      type: ShapesReducerActionType.UPDATE_SHAPE;
      payload: ShapeUpdatePayload;
    }
  | {
      type: ShapesReducerActionType.DELETE_SHAPE;
      payload: {
        pageNumber: number;
        id: string;
      };
    }
  | {
      type: ShapesReducerActionType.DUPLICATE_SHAPE;
      payload: {
        pageNumber: number;
        shapeId: string;
        newShapeId?: string;
      };
    };

const shapesReducer = (state: AppState, action: ShapesReducerAction) => {
  switch (action.type) {
    case ShapesReducerActionType.ADD_SHAPE: {
      const { points, pageNumber, type, borderColor, backgroundColor, borderWidth, isWhiteout } = action.payload;
      const id = generateId();

      const newState: AppState = { ...state };
      let shape: Shape | undefined;

      if (type === ShapeTypes.Rectangle || type === ShapeTypes.Ellipse) {
        shape = {
          id,
          type,
          canvasPoints: points,
          pdfPoints: canvasPointsToPdfPoints(points, state.pagesMetadata[pageNumber].page, state.scale),
          backgroundColor: backgroundColor ?? RGB_COLORS.TRANSPARENT,
          borderColor: borderColor ?? RGB_COLORS.BLACK,
          borderWidth: borderWidth ?? 2,
          // Only rectangles can be whiteouts; an ellipse never locks text.
          ...(type === ShapeTypes.Rectangle && isWhiteout ? { isWhiteout: true } : {}),
        };
      } else if (type === ShapeTypes.Line || type === ShapeTypes.Arrow) {
        shape = {
          id,
          type,
          canvasPoints: points,
          pdfPoints: canvasPointsToPdfPoints(points, state.pagesMetadata[pageNumber].page, state.scale),
          backgroundColor: backgroundColor ?? RGB_COLORS.RED,
          borderColor: undefined,
          borderWidth: borderWidth ?? 4,
        };
      }

      const pageShapes = shape ? [...newState.shapes[pageNumber], shape] : newState.shapes[pageNumber];

      return {
        ...addObjectToAppState(newState, id),
        shapes: {
          ...newState.shapes,
          [pageNumber]: pageShapes,
        },
      };
    }
    case ShapesReducerActionType.UPDATE_SHAPE: {
      const { pageNumber, shape } = action.payload;
      const page = state.pagesMetadata[pageNumber].page;
      const oldShape = state.shapes[pageNumber].find((v) => v.id === shape.id);

      if (oldShape) {
        const newShape = { ...oldShape };
        if (shape.canvasPoints) {
          newShape.canvasPoints = shape.canvasPoints;
          newShape.pdfPoints = canvasPointsToPdfPoints(shape.canvasPoints, page, state.scale);
        }
        if (shape.backgroundColor) {
          newShape.backgroundColor = shape.backgroundColor;
        }
        if (shape.borderWidth !== undefined && shape.borderWidth >= 0) {
          newShape.borderWidth = shape.borderWidth;
        }
        if (newShape.type === ShapeTypes.Ellipse || newShape.type === ShapeTypes.Rectangle) {
          if (shape.borderColor) {
            newShape.borderColor = shape.borderColor;
          }
        }

        const newShapes = state.shapes[pageNumber].map((s) => (s.id === shape.id ? newShape : s));

        return {
          ...state,
          shapes: { ...state.shapes, [pageNumber]: newShapes },
        };
      }

      return state;
    }
    case ShapesReducerActionType.DELETE_SHAPE: {
      const { pageNumber, id } = action.payload;

      const newShapes = state.shapes[pageNumber].filter((shape: Shape) => shape.id !== id);

      return {
        ...state,
        shapes: { ...state.shapes, [pageNumber]: newShapes },
      };
    }
    case ShapesReducerActionType.DUPLICATE_SHAPE: {
      const { pageNumber, shapeId, newShapeId } = action.payload;
      const shapes = state.shapes;

      const selectedShape = shapes[pageNumber].find((shape: Shape) => shape.id === shapeId);
      if (!selectedShape) return state;

      const newId = newShapeId ?? generateId();

      const isLine = selectedShape.type === ShapeTypes.Line || selectedShape.type === ShapeTypes.Arrow;

      const newCanvasPoints = selectedShape.canvasPoints.map((point: Point) => ({
        x: point.x + (isLine ? 0 : DUPLICATE_SHAPE_OFFSET),
        y: point.y + DUPLICATE_SHAPE_OFFSET,
      }));

      // TODO: Fix this to add offset to the position of the shape

      const page = state.pagesMetadata[pageNumber].page;
      const scale = state.scale;

      const newShapes = [
        ...shapes[pageNumber],
        {
          ...selectedShape,
          id: newId,
          canvasPoints: newCanvasPoints,
          pdfPoints: canvasPointsToPdfPoints(newCanvasPoints, page, scale),
        },
      ] as Shape[];

      return {
        ...state,
        shapes: { ...state.shapes, [pageNumber]: newShapes },
      };
    }
    default:
      return state;
  }
};

export default shapesReducer;
